/**
 * Vault repository (Vault layer).
 *
 * The high-level API over the IndexedDB store + envelope crypto. Everything the
 * app persists goes through here. Plaintext facets are sealed BEFORE they touch
 * storage, and only envelopes are ever returned for sync. See
 * docs/ARCHITECTURE.md §4–§5.
 *
 * Pure TypeScript. No React imports.
 */

import {
  getVaultDB,
  type VaultIdentity,
  type VaultEnvelopeRecord,
  type VaultVersionRecord,
  type VaultSessionRecord,
  type VaultAuditRecord,
  type VaultGrantRecord,
  type VaultAttributionRecord,
  type VaultEntitlementRecord,
} from './db';
import type { Entitlement } from '@/core/entitlement/EntitlementProvider';
import {
  sealFacet,
  openFacet,
  rotateFacetKey,
  rewrapForGrantee,
  type FacetEnvelope,
  type KeyWrapper,
} from './envelope';
import {
  FACET_KEYS,
  type FacetKey,
  type SoulGraph,
  type SoulGraphVersion,
} from '@/core/graph/schema';

/** Generate an opaque random identifier (hex) for sync `d` tags and ids. */
export function randomId(bytes = 16): string {
  const arr = crypto.getRandomValues(new Uint8Array(bytes));
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

/* --------------------------------- identity -------------------------------- */

export async function putIdentity(identity: VaultIdentity): Promise<void> {
  const db = await getVaultDB();
  await db.put('identity', identity);
}

export async function getIdentity(pubkey: string): Promise<VaultIdentity | undefined> {
  const db = await getVaultDB();
  return db.get('identity', pubkey);
}

export async function markBackedUp(pubkey: string): Promise<void> {
  const existing = await getIdentity(pubkey);
  if (!existing) return;
  await putIdentity({ ...existing, backed_up: true });
}

/* ---------------------------------- facets --------------------------------- */

function envelopeId(owner: string, versionId: string, facet: FacetKey): string {
  return `${owner}:${versionId}:${facet}`;
}

/**
 * Seal and persist a single facet for a version. Sealing happens here, so the
 * plaintext payload never reaches storage or any publish path.
 */
export async function saveFacet(
  owner: string,
  versionId: string,
  facet: FacetKey,
  payload: unknown,
  wrapper: KeyWrapper,
): Promise<VaultEnvelopeRecord> {
  const envelope = await sealFacet(payload, owner, wrapper);
  const record: VaultEnvelopeRecord = {
    id: envelopeId(owner, versionId, facet),
    owner_pubkey: owner,
    version_id: versionId,
    facet,
    envelope,
    sync_d: randomId(),
    updated_at: Math.floor(Date.now() / 1000),
  };
  const db = await getVaultDB();
  await db.put('envelopes', record);
  return record;
}

/** Load and decrypt a single facet for the owner. */
export async function loadFacet<T = unknown>(
  owner: string,
  versionId: string,
  facet: FacetKey,
  wrapper: KeyWrapper,
): Promise<T | null> {
  const db = await getVaultDB();
  const record = await db.get('envelopes', envelopeId(owner, versionId, facet));
  if (!record) return null;
  return openFacet<T>(record.envelope, owner, wrapper);
}

/** List all envelope records for an owner (encrypted — safe to enumerate). */
export async function listEnvelopes(owner: string): Promise<VaultEnvelopeRecord[]> {
  const db = await getVaultDB();
  return db.getAllFromIndex('envelopes', 'by_owner', owner);
}

/** Rotate a facet's content key, re-wrapping only for the kept grantees. */
export async function rotateFacet(
  owner: string,
  versionId: string,
  facet: FacetKey,
  wrapper: KeyWrapper,
  keepGrantees: string[] = [],
): Promise<VaultEnvelopeRecord | null> {
  const db = await getVaultDB();
  const record = await db.get('envelopes', envelopeId(owner, versionId, facet));
  if (!record) return null;
  const rotated = await rotateFacetKey(record.envelope, owner, wrapper, keepGrantees);
  const updated: VaultEnvelopeRecord = {
    ...record,
    envelope: rotated,
    sync_d: randomId(), // new opaque id so the old synced copy is orphaned
    updated_at: Math.floor(Date.now() / 1000),
  };
  await db.put('envelopes', updated);
  return updated;
}

/** Re-wrap a facet's content key for an additional grantee. */
export async function shareFacet(
  owner: string,
  versionId: string,
  facet: FacetKey,
  granteePubkey: string,
  wrapper: KeyWrapper,
): Promise<FacetEnvelope | null> {
  const db = await getVaultDB();
  const record = await db.get('envelopes', envelopeId(owner, versionId, facet));
  if (!record) return null;
  const rewrapped = await rewrapForGrantee(record.envelope, owner, granteePubkey, wrapper);
  await db.put('envelopes', { ...record, envelope: rewrapped });
  return rewrapped;
}

/* --------------------------------- versions -------------------------------- */

export async function putVersion(record: VaultVersionRecord): Promise<void> {
  const db = await getVaultDB();
  await db.put('versions', record);
}

export async function listVersions(owner: string): Promise<VaultVersionRecord[]> {
  const db = await getVaultDB();
  const all = await db.getAllFromIndex('versions', 'by_owner', owner);
  return all.sort((a, b) => a.sequence - b.sequence);
}

/**
 * Point the owner's "current" SoulGraph at a version. The current graph is
 * always a pointer, never a mutation (docs/ARCHITECTURE.md §3), so this only
 * flips the `is_current` flag — no version is ever overwritten.
 */
export async function setCurrentVersion(owner: string, versionId: string): Promise<void> {
  const db = await getVaultDB();
  const all = await db.getAllFromIndex('versions', 'by_owner', owner);
  const tx = db.transaction('versions', 'readwrite');
  for (const record of all) {
    const shouldBeCurrent = record.version_id === versionId;
    if (record.is_current !== shouldBeCurrent) {
      await tx.store.put({ ...record, is_current: shouldBeCurrent });
    }
  }
  await tx.done;
}

/**
 * Persist a complete, immutable SoulGraph version: seal every non-null facet
 * into its own envelope, write the version record, and (by default) make it the
 * current pointer. Sealing happens here so plaintext never reaches storage.
 *
 * Returns the number of facets sealed.
 */
export async function saveGraphVersion(
  version: SoulGraphVersion,
  wrapper: KeyWrapper,
  options: { makeCurrent?: boolean } = {},
): Promise<number> {
  const makeCurrent = options.makeCurrent ?? true;
  const owner = version.graph.identity.pubkey;
  const versionId = version.version_id;

  // Seal each non-null facet (identity is public-ish metadata — never an envelope).
  let sealed = 0;
  for (const facet of FACET_KEYS) {
    if (facet === 'identity') continue;
    const payload = version.graph[facet as keyof SoulGraph];
    if (!payload) continue;
    await saveFacet(owner, versionId, facet, payload, wrapper);
    sealed += 1;
  }

  // Write the immutable version record.
  const record: VaultVersionRecord = {
    id: `${owner}:${versionId}`,
    owner_pubkey: owner,
    version_id: versionId,
    sequence: version.sequence,
    created_at: version.graph.created_at,
    is_current: false, // set by setCurrentVersion below to keep one source of truth
    confidence: version.graph.confidence,
    provenance: version.graph.provenance,
    display_name: version.graph.identity.display_name,
  };
  await putVersion(record);

  if (makeCurrent) {
    await setCurrentVersion(owner, versionId);
  }

  return sealed;
}

/* -------------------------------- sessions --------------------------------- */

/**
 * Persist a raw assessment session (the actual responses) encrypted to self.
 * The responses themselves never leave the device unencrypted; only their hash
 * is recorded in the graph's provenance.
 */
export async function saveSession(
  owner: string,
  payload: unknown,
  wrapper: KeyWrapper,
  options: { id?: string; completed?: boolean } = {},
): Promise<VaultSessionRecord> {
  const envelope = await sealFacet(payload, owner, wrapper);
  const now = Math.floor(Date.now() / 1000);
  const record: VaultSessionRecord = {
    id: options.id ?? randomId(),
    owner_pubkey: owner,
    envelope,
    created_at: now,
    completed_at: options.completed ? now : undefined,
  };
  const db = await getVaultDB();
  await db.put('sessions', record);
  return record;
}

/** List all raw assessment sessions for an owner (newest first). */
export async function listSessions(owner: string): Promise<VaultSessionRecord[]> {
  const db = await getVaultDB();
  const all = await db.getAllFromIndex('sessions', 'by_owner', owner);
  return all.sort((a, b) => b.created_at - a.created_at);
}

/** Load and decrypt a single session's raw responses. */
export async function loadSession<T = unknown>(
  owner: string,
  sessionId: string,
  wrapper: KeyWrapper,
): Promise<T | null> {
  const db = await getVaultDB();
  const record = await db.get('sessions', sessionId);
  if (!record || record.owner_pubkey !== owner) return null;
  return openFacet<T>(record.envelope, owner, wrapper);
}

/* ----------------------------- in-progress draft --------------------------- */
/*
 * A single resumable draft per owner (the "continue where you left off" state
 * for the full assessment). Stored encrypted, like any session, under a stable
 * id so it can be found, overwritten, and cleared on completion.
 */

function progressId(owner: string): string {
  return `${owner}:progress`;
}

/** Save (overwrite) the owner's in-progress full-assessment answers, encrypted. */
export async function saveProgress(
  owner: string,
  payload: unknown,
  wrapper: KeyWrapper,
): Promise<void> {
  const envelope = await sealFacet(payload, owner, wrapper);
  const db = await getVaultDB();
  await db.put('sessions', {
    id: progressId(owner),
    owner_pubkey: owner,
    envelope,
    created_at: Math.floor(Date.now() / 1000),
    completed_at: undefined,
  });
}

/** Load the owner's in-progress draft, or null if there is none. */
export async function loadProgress<T = unknown>(
  owner: string,
  wrapper: KeyWrapper,
): Promise<T | null> {
  return loadSession<T>(owner, progressId(owner), wrapper);
}

/** Whether an in-progress draft exists (no decryption needed). */
export async function hasProgress(owner: string): Promise<boolean> {
  const db = await getVaultDB();
  const record = await db.get('sessions', progressId(owner));
  return Boolean(record);
}

/** Remove the in-progress draft (e.g. after completion). */
export async function clearProgress(owner: string): Promise<void> {
  const db = await getVaultDB();
  await db.delete('sessions', progressId(owner));
}

/**
 * Collect the owner's known prior answers across completed sessions, so the
 * full assessment never re-asks a demo (or earlier) item. Returns a flat
 * map of item_id → value. Requires decryption, so a wrapper is needed.
 */
export async function loadPriorAnswers(
  owner: string,
  wrapper: KeyWrapper,
): Promise<Record<string, number | 'A' | 'B' | string[]>> {
  const sessions = await listSessions(owner);
  const answers: Record<string, number | 'A' | 'B' | string[]> = {};
  // Oldest first so newer answers win on conflict.
  const completed = sessions
    .filter((s) => s.completed_at)
    .sort((a, b) => (a.completed_at ?? 0) - (b.completed_at ?? 0));
  for (const session of completed) {
    try {
      const set = await openFacet<{ responses?: { item_id: string; value: number | 'A' | 'B' | string[] }[] }>(
        session.envelope,
        owner,
        wrapper,
      );
      for (const r of set.responses ?? []) {
        answers[r.item_id] = r.value;
      }
    } catch {
      // Skip any session we can't open; never block the user on one bad record.
    }
  }
  return answers;
}

/* --------------------------------- grants ---------------------------------- */

export async function listGrants(owner: string): Promise<VaultGrantRecord[]> {
  const db = await getVaultDB();
  return db.getAllFromIndex('grants', 'by_owner', owner);
}

/* ---------------------------------- audit ---------------------------------- */

export async function appendAudit(
  owner: string,
  action: string,
  detail?: string,
): Promise<void> {
  const db = await getVaultDB();
  const record: VaultAuditRecord = {
    id: randomId(),
    owner_pubkey: owner,
    at: Math.floor(Date.now() / 1000),
    action,
    detail,
  };
  await db.put('audit', record);
}

export async function listAudit(owner: string): Promise<VaultAuditRecord[]> {
  const db = await getVaultDB();
  const all = await db.getAllFromIndex('audit', 'by_owner', owner);
  return all.sort((a, b) => b.at - a.at);
}

/* ------------------------------- attribution ------------------------------- */

export async function bindAttribution(
  owner: string,
  attribution: {
    ref?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    captured_at: number;
  },
): Promise<void> {
  const db = await getVaultDB();
  const existing = await db.get('attribution', owner);
  if (existing) return; // first-touch wins
  await db.put('attribution', { owner_pubkey: owner, ...attribution });
}

/** Read the attribution bound to an owner, or undefined if none. */
export async function getBoundAttribution(
  owner: string,
): Promise<VaultAttributionRecord | undefined> {
  const db = await getVaultDB();
  return db.get('attribution', owner);
}

/* ------------------------------ entitlements ------------------------------- */

function entitlementId(owner: string, productId: string): string {
  return `${owner}:${productId}`;
}

/**
 * Cache a verified entitlement so the app can answer access questions instantly
 * and offline. The zap receipt on relays remains the source of truth; this is
 * a cache that is re-confirmed on new devices by re-querying relays.
 */
export async function cacheEntitlement(entitlement: Entitlement): Promise<void> {
  const db = await getVaultDB();
  const record: VaultEntitlementRecord = {
    id: entitlementId(entitlement.owner_pubkey, entitlement.product_id),
    owner_pubkey: entitlement.owner_pubkey,
    product_id: entitlement.product_id,
    price_version: entitlement.price_version,
    source: entitlement.source,
    issued_at: entitlement.issued_at,
    expires_at: entitlement.expires_at,
    proof_ref: entitlement.proof_ref,
    verified_at: Math.floor(Date.now() / 1000),
  };
  await db.put('entitlements', record);
}

/** Read a single cached entitlement for an owner + product, or undefined. */
export async function getCachedEntitlement(
  owner: string,
  productId: string,
): Promise<VaultEntitlementRecord | undefined> {
  const db = await getVaultDB();
  return db.get('entitlements', entitlementId(owner, productId));
}

/** List all cached entitlements for an owner. */
export async function listCachedEntitlements(
  owner: string,
): Promise<VaultEntitlementRecord[]> {
  const db = await getVaultDB();
  return db.getAllFromIndex('entitlements', 'by_owner', owner);
}

/** Remove a cached entitlement (e.g. after a failed re-verification). */
export async function clearCachedEntitlement(
  owner: string,
  productId: string,
): Promise<void> {
  const db = await getVaultDB();
  await db.delete('entitlements', entitlementId(owner, productId));
}
